import { create } from 'zustand';

/**
 * Communities the member has asked to join and is still waiting on.
 *
 * Core only knows about a community once the member is approved into it — a pending request
 * lives in that community's own database and nothing links it to the SSO identity — so the
 * Communities list has no way to show it. The app records the request when it submits one, so
 * the community appears in the drawer with its real state instead of vanishing until approval.
 *
 * Recorded on this device only. A reinstall or a second device loses the entry and the community
 * goes back to being invisible until approval; `reconcile` is the other half — an entry that has
 * since been approved appears in `linkedTenants`, and is dropped from here.
 *
 * Persisted under a `paas_*` key so the native durability mirror keeps it across app kills.
 */

const STORAGE_KEY = 'paas_pending_joins';

export interface PendingJoin {
  tenantId: string;
  name: string;
  domain?: string;
  logoUrl?: string;
  /** ISO timestamp of the request, shown on the "in the queue" screen. */
  submittedAt: string;
  /**
   * Whether the community's onboarding still needs finishing. Last known state, not truth —
   * the pending screen re-reads the gate on every visit and corrects it. Drives the drawer
   * sublabel, so a member who walked out mid-flow is told to finish rather than to wait.
   */
  onboardingPending?: boolean;
}

function load(): PendingJoin[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]');
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((j): j is PendingJoin => !!j && typeof j.tenantId === 'string' && typeof j.name === 'string');
  } catch {
    return [];
  }
}

function save(joins: PendingJoin[]): void {
  try {
    // Nothing pending is the absence of the key, not an empty array — the signed-out residue
    // sweep is asserted on which keys survive it.
    if (joins.length === 0) localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, JSON.stringify(joins));
  } catch (error) {
    console.warn('[pending-joins] persist failed:', error);
  }
}

interface PendingJoinsState {
  pending: PendingJoin[];
  find: (tenantId: string) => PendingJoin | undefined;
  add: (join: PendingJoin) => void;
  /** Correct the stored flow state — the pending screen and the runner both know better. */
  setOnboardingPending: (tenantId: string, onboardingPending: boolean) => void;
  remove: (tenantId: string) => void;
  /** Drop anything the member has since been approved into, or that is no longer theirs. */
  reconcile: (linkedTenantIds: string[]) => void;
  clear: () => void;
}

export const usePendingJoinsStore = create<PendingJoinsState>((set, get) => ({
  pending: load(),

  find(tenantId) {
    return get().pending.find((j) => j.tenantId === tenantId);
  },

  add(join) {
    const next = [...get().pending.filter((j) => j.tenantId !== join.tenantId), join];
    set({ pending: next });
    save(next);
  },

  setOnboardingPending(tenantId, onboardingPending) {
    const current = get().pending;
    if (!current.some((j) => j.tenantId === tenantId && j.onboardingPending !== onboardingPending)) return;
    const next = current.map((j) => (j.tenantId === tenantId ? { ...j, onboardingPending } : j));
    set({ pending: next });
    save(next);
  },

  remove(tenantId) {
    const next = get().pending.filter((j) => j.tenantId !== tenantId);
    if (next.length === get().pending.length) return;
    set({ pending: next });
    save(next);
  },

  reconcile(linkedTenantIds) {
    const linked = new Set(linkedTenantIds);
    const next = get().pending.filter((j) => !linked.has(j.tenantId));
    if (next.length === get().pending.length) return;
    set({ pending: next });
    save(next);
  },

  clear() {
    set({ pending: [] });
    save([]);
  },
}));
